import { useState } from "react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@tremor/react";
import { TextInput, Select, SelectItem } from "@tremor/react";
import { UserAddIcon } from "@heroicons/react/outline";
import useAxiosPrivate, {
	toastErrorUnlessHandled,
} from "@/hooks/useAxiosPrivate";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clipboard, UserPlus } from "lucide-react";

const InviteNbUser = ({ getNbUsers }: { getNbUsers?: any }) => {
	const [email, setEmail] = useState("");
	const [role, setRole] = useState("user");
	const [popOpen, setPopOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [activationLink, setActivationLink] = useState("");
	const axiosPrivate = useAxiosPrivate();

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setIsLoading(true);

		try {
			const response = await axiosPrivate.post(
				"/auth/nbUsers/invite",
				{ email, role },
				{
					headers: { "Content-Type": "application/json" },
					withCredentials: true,
				},
			);

			// Save the activation link to display to admin
			if (response.data?.activationLink) {
				setActivationLink(response.data.activationLink);
			}

			toast.success(`Ο χρήστης ${email} προσκλήθηκε με επιτυχία.`);

			// If getNbUsers was provided, refresh the users list
			if (getNbUsers) {
				await getNbUsers();
			}
		} catch (error: any) {
			console.error(error);
			if (error.response?.status === 403) {
				toast.error("Δεν έχετε δικαίωμα πρόσκλησης νέου χρήστη συστήματος.");
				return;
			}
			toastErrorUnlessHandled(
				error,
				error.response?.data?.message ||
					"Σφάλμα κατά την πρόσκληση του χρήστη.",
			);
		} finally {
			setIsLoading(false);
		}
	}

	const copyToClipboard = () => {
		navigator.clipboard
			.writeText(activationLink)
			.then(() => toast.success("Ο σύνδεσμος αντιγράφηκε στο πρόχειρο"))
			.catch(() => toast.error("Δεν ήταν δυνατή η αντιγραφή του συνδέσμου"));
	};

	const resetForm = () => {
		setEmail("");
		setRole("user");
		setActivationLink("");
		setPopOpen(false);
	};

	return (
		<Popover open={popOpen} onOpenChange={setPopOpen}>
			<PopoverTrigger asChild>
				<Button
					className="ml-2"
					icon={UserAddIcon}
					onClick={() => setPopOpen(true)}
				>
					Πρόσκληση Νέου Χρήστη
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-96"
				onPointerDownOutside={(e) => {
					// Prevent closing if we have an activation link to show
					if (activationLink) {
						e.preventDefault();
					}
				}}
			>
				{!activationLink ? (
					<div className="space-y-4">
						<div className="space-y-2">
							<h4 className="font-medium leading-none">Πρόσκληση Χρήστη</h4>
							<p className="text-sm text-muted-foreground">
								Εισάγετε το email του χρήστη που θέλετε να προσκαλέσετε.
							</p>
						</div>
						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email">Email</Label>
								<TextInput
									id="email"
									type="email"
									placeholder="example@example.com"
									value={email}
									onValueChange={setEmail}
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="role">Ρόλος</Label>
								<Select id="role" value={role} onValueChange={setRole}>
									<SelectItem value="user">Χρήστης</SelectItem>
									<SelectItem value="admin">Διαχειριστής</SelectItem>
								</Select>
							</div>
							<div className="flex justify-end space-x-2">
								<Button color="red" onClick={() => setPopOpen(false)}>
									Ακύρωση
								</Button>
								<Button
									color="emerald"
									type="submit"
									loading={isLoading}
									icon={UserPlus}
								>
									Αποστολή Πρόσκλησης
								</Button>
							</div>
						</form>
					</div>
				) : (
					<div className="space-y-4">
						<div className="space-y-2">
							<h4 className="font-medium leading-none">Πρόσκληση Στάλθηκε</h4>
							<p className="text-sm text-muted-foreground">
								Η πρόσκληση για τον χρήστη {email} δημιουργήθηκε με επιτυχία.
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="activationLink">Σύνδεσμος Ενεργοποίησης</Label>
							<div className="flex">
								<Input
									id="activationLink"
									value={activationLink}
									readOnly
									className="pr-10"
								/>
								<Button
									color="blue"
									size="xs"
									icon={Clipboard}
									className="-ml-10"
									onClick={copyToClipboard}
								>
									Αντιγραφή
								</Button>
							</div>
							<p className="text-sm text-muted-foreground">
								Αντιγράψτε αυτόν τον σύνδεσμο και αποστείλτε τον στον χρήστη για
								να ολοκληρώσει την εγγραφή του.
							</p>
						</div>
						<div className="flex justify-end">
							<Button onClick={resetForm}>Κλείσιμο</Button>
						</div>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
};

export default InviteNbUser;
